import ApplicationRow from '@/components/shared/ApplicationRow';
import ApplicationTableHeader from '@/components/shared/ApplicationTableHeader';
import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import HistoryModal from '@/components/sharedModals/HistoryModal';
import { CERTIFICATE_COMMON_FIELDS, COV_CERTIFICATE_FIELDS, CSAW_CERTIFICATE_FIELDS } from '@/graphql/fragments/certificateFragments';
import useActionHotkeys from '@/hooks/useActionHotkeys';
import useEscapeKey from '@/hooks/useEscapeKey';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ClipboardCheck, Eye, FileOutput, FileSignature, FileText, History, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import InspectionReportReviewModal from '../../InspectionDashboardComponents/InspectionReportReviewModal';
import ChiefReviewModal from './ChiefReviewModal';
import ChiefTSDSignatureModal from './ChiefTSDSignatureModal';

const DELETE_OOP = gql`
   mutation DeleteOOP($applicationId: String!) {
      deleteOOP(applicationId: $applicationId) {
         _id
         applicationId
      }
   }
`;

const UNDO_OOP_CREATION = gql`
   mutation UndoOOPCreation($id: ID!) {
      undoOOPCreation(id: $id) {
         id
         awaitingOOP
         OOPCreated
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $reviewedByChief: Boolean
      $awaitingOOP: Boolean
      $certificateSignedByChiefTSD: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         reviewedByChief: $reviewedByChief
         awaitingOOP: $awaitingOOP
         certificateSignedByChiefTSD: $certificateSignedByChiefTSD
      ) {
         id
         currentStage
         status
         reviewedByChief
         awaitingOOP
         history {
         notes
         timestamp
         }
      }
   }
`;

const GET_CERTIFICATE = gql`
   ${CERTIFICATE_COMMON_FIELDS}
   ${COV_CERTIFICATE_FIELDS}
   ${CSAW_CERTIFICATE_FIELDS}
   query GetCertificatesByApplicationId($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         ...CertificateCommonFields
         ...COVCertificateFields
         ...CSAWCertificateFields
      }
   }
`;


const UPDATE_CERTIFICATE = gql`
   mutation UpdateCertificate($id: ID!, $certificateStatus: String) {
      updateCertificate(id: $id, certificateStatus: $certificateStatus) {
         id
         certificateStatus
      }
   }
`;

const DELETE_CERTIFICATE = gql`
   mutation DeleteCertificate($id: ID!) {
      deleteCertificate(id: $id) {
         success
         message
      }
   }
`;

const UNDO_SIGNATURE_CHIEF_TSD = gql`
   mutation UpdatePermitStage(
      $id: ID!
      $currentStage: String!
      $status: String!
      $certificateSignedByChiefTSD: Boolean!
   ) {
      updatePermitStage(
         id: $id
         currentStage: $currentStage
         status: $status
         certificateSignedByChiefTSD: $certificateSignedByChiefTSD
      ) {
         id
         currentStage
         status
         certificateSignedByChiefTSD
      }
   }
`;

const ChiefApplicationRow = ({ app, onReviewComplete, currentTab, isMobile, isFirstRow }) => {
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
   const [isChiefTSDSignatureModalOpen, setIsChiefTSDSignatureModalOpen] = useState(false);
   const [isViewCertModalOpen, setIsViewCertModalOpen] = useState(false);
   const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [undoOOPCreation] = useMutation(UNDO_OOP_CREATION);
   const [deleteOOP] = useMutation(DELETE_OOP);
   const [undoSignatureChiefTSD] = useMutation(UNDO_SIGNATURE_CHIEF_TSD);
   const navigate = useNavigate();

   const handleViewClick = () => setIsViewModalOpen(true);
   const handleReviewClick = () => setIsReviewModalOpen(true);
   const handleReviewComplete = () => {
      setIsReviewModalOpen(false);
      onReviewComplete();
   };

   const [updateCertificate] = useMutation(UPDATE_CERTIFICATE);
   const [deleteCertificate] = useMutation(DELETE_CERTIFICATE);

   const { data: certData, loading: certLoading, error: certError } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: app.id },
      skip: !app.id
   });

   const handleUndoOOP = async () => {
      try {
         await deleteOOP({
            variables: {
               applicationId: app.id
            }
         });

         await undoOOPCreation({
            variables: {
               id: app.id
            }
         });

         toast.success('OOP creation undone successfully');
         onReviewComplete();
      } catch (error) {
         console.error('Error undoing OOP creation:', error);
         toast.error('Failed to undo OOP creation');
      }
   };

   const handleUndo = async () => {
      try {
         if (currentTab === 'Completed Reviews') {
            await updatePermitStage({
               variables: {
                  id: app.id,
                  currentStage: 'ChiefRPSReview',
                  status: 'In Progress',
                  notes: 'Review undone by Chief RPS/TSD',
                  reviewedByChief: false,
                  awaitingOOP: false,
               }
            });
         } else if (currentTab === 'Signed Certificates') {
            await updateCertificate({
               variables: {
                  id: certData.id,
                  currentStage: 'PendingChiefTSDSignature',
                  certificateSignedByChiefTSD: false
               }
            });
         }
         toast.success('Application review undone successfully');
         onReviewComplete();

      } catch (error) {
         console.error('Error undoing review:', error);
         toast.error('Failed to undo review');
      }
   };

   const handleUndoSignature = async () => {
      try {
         await updatePermitStage({
            variables: {
               id: app.id,
               currentStage: 'PendingChiefTSDSignature',
               status: 'In Progress',
               certificateSignedByChiefTSD: false
            }
         });

         toast.success('Signature undone successfully');
         onReviewComplete();
      } catch (error) {
         console.error('Error undoing signature:', error);
         toast.error('Failed to undo signature');
      }
   };

   const actions = [
      {
         icon: Eye,
         label: "View Application",
         onClick: () => setIsViewModalOpen(true),
         variant: "outline",
         className: "view-action",
         hotkey: "Ctrl + 1"
      },
      {
         icon: History,
         label: "View History",
         onClick: () => setIsHistoryModalOpen(true),
         variant: "outline",
         className: "history-action text-purple-600",
         hotkey: "Ctrl + 2"
      },
      currentTab === 'Awaiting OOP' && {
         icon: FileOutput,
         label: "Create Order of Payment Form",
         onClick: () => navigate('/personnel/order-of-payment', { state: { applicationId: app.id } }),
         variant: "outline",
         className: "create-oop-action text-green-600",
         hotkey: "Ctrl + 3"
      },
      currentTab === 'Reports for Review' && {
         icon: ClipboardCheck,
         label: "View Inspection Report",
         onClick: () => setIsInspectionModalOpen(true),
         variant: "outline",
         className: "view-report-action text-purple-600",
         hotkey: "Ctrl + 3"
      },
      currentTab === 'Pending Chief TSD Signature' && {
         icon: FileSignature,
         label: "Sign Certificate",
         onClick: () => setIsChiefTSDSignatureModalOpen(true),
         variant: "outline",
         className: "sign-action text-blue-600",
         hotkey: "Ctrl + 4"
      },
      (currentTab === 'Pending Chief TSD Signature' ||
         currentTab === 'Signed Certificates' ||
         currentTab === 'Released Certificates' ||
         currentTab === 'Completed Applications' ||
         (app.hasCertificate && certData?.getCertificatesByApplicationId?.length > 0)) && {
         icon: FileText,
         label: "View Certificate",
         onClick: () => setIsViewCertModalOpen(true),
         variant: "outline",
         className: "view-certificate-action text-purple-600"
      },
      currentTab === 'Signed Certificates' && {
         icon: RotateCcw,
         label: "Undo Signature",
         onClick: handleUndoSignature,
         variant: "outline",
         className: "undo-action text-yellow-600"
      },
      currentTab === 'Completed Reviews' && {
         icon: RotateCcw,
         label: "Undo Review",
         onClick: handleUndo,
         variant: "outline",
         className: "undo-action text-yellow-600"
      },
      currentTab === 'Created OOP' && {
         icon: RotateCcw,
         label: "Undo OOP Creation",
         onClick: handleUndoOOP,
         variant: "outline",
         className: "undo-action text-yellow-600"
      }
   ].filter(Boolean);

   useActionHotkeys({
      actions,
      activeMainTab: currentTab,
      disabled: isViewModalOpen || isReviewModalOpen || isInspectionModalOpen ||
         isChiefTSDSignatureModalOpen || isViewCertModalOpen || isHistoryModalOpen
   });

   useEscapeKey(() => setIsViewModalOpen(false), isViewModalOpen);
   useEscapeKey(() => setIsReviewModalOpen(false), isReviewModalOpen);
   useEscapeKey(() => setIsInspectionModalOpen(false), isInspectionModalOpen);
   useEscapeKey(() => setIsChiefTSDSignatureModalOpen(false), isChiefTSDSignatureModalOpen);
   useEscapeKey(() => setIsViewCertModalOpen(false), isViewCertModalOpen);
   useEscapeKey(() => setIsHistoryModalOpen(false), isHistoryModalOpen);

   return (
      <>
         {isFirstRow && !isMobile && <ApplicationTableHeader />}
         <ApplicationRow
            application={app}
            actions={actions}
            isMobile={isMobile}
         />

         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={app}
         />
         <ChiefReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            application={app}
            onReviewComplete={handleReviewComplete}
         />
         <InspectionReportReviewModal
            isOpen={isInspectionModalOpen}
            onClose={() => setIsInspectionModalOpen(false)}
            application={app}
         />

         <ChiefTSDSignatureModal
            isOpen={isChiefTSDSignatureModalOpen}
            onClose={() => setIsChiefTSDSignatureModalOpen(false)}
            application={{
               ...app,
               certificateId: certData?.getCertificatesByApplicationId?.[0]?.id
            }}
            onSignComplete={onReviewComplete}
         />
         <CertificateViewModal
            isOpen={isViewCertModalOpen}
            onClose={() => setIsViewCertModalOpen(false)}
            certificate={certData?.getCertificatesByApplicationId[0]}
            loading={certLoading}
            error={certError}
         />
         <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            history={app.history}
         />
      </>
   );
};

export default ChiefApplicationRow;
